import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppEvent, Attendee, CheckinDate, CustomField, Group, Printer, Rotation } from './types';
import {
  getAllCustomFieldsQuery,
  getAllEventsQuery,
  getAllPrintersQuery,
  getAttendeesForEventQuery,
  getCheckInDatesForEventQuery,
  getGroupsForEventQuery,
  getRotationsForEventQuery,
} from './firebase';
import { onSnapshot } from 'firebase/firestore';
import { NO_PRINT_ID } from './constants';
import { useToast } from './toast-context';

export const FirebaseContext = createContext<{
  events: AppEvent[];
  activeEvent?: AppEvent;
  attendees: Attendee[];
  checkInDates: CheckinDate[];
  activeCheckInDate?: CheckinDate;
  printers: Printer[];
  groups: Group[];
  rotations: Rotation[];
  customFields: CustomField[];
  // Optimistic local removal: drops the attendee with the given ref from
  // the in-memory list immediately so callers don't have to wait for the
  // Firestore snapshot to propagate. The next server snapshot will reaffirm
  // the same state.
  removeAttendeeLocally: (ref: string) => void;
}>({
  events: [],
  activeEvent: undefined,
  attendees: [],
  checkInDates: [],
  activeCheckInDate: undefined,
  printers: [],
  groups: [],
  rotations: [],
  customFields: [],
  removeAttendeeLocally: () => {},
});

export const FirebaseProvider = ({ children }: { children: React.ReactNode }) => {
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [checkInDates, setcheckInDates] = useState<CheckinDate[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [rotations, setRotations] = useState<Rotation[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const toast = useToast();

  // Captures the active event ref from the very first events snapshot, so
  // the change-detection effect below treats it as the baseline (not a
  // transition). Set inside the onSnapshot callback so we know exactly when
  // the listener has delivered its initial state.
  const prevActiveEventRefRef = useRef<string | null | undefined>(undefined);

  // Events, printers, and custom-fields are global; subscribe once for the
  // lifetime of the provider.
  useEffect(() => {
    const removeEventsListener = onSnapshot(
      getAllEventsQuery,
      (snapshot) => {
        const res: AppEvent[] = [];
        snapshot.forEach((doc) => {
          res.push({ ref: doc.id, ...doc.data() } as AppEvent);
        });
        if (prevActiveEventRefRef.current === undefined) {
          prevActiveEventRefRef.current =
            res.find((e) => e.isActive)?.ref ?? null;
        }
        setEvents(res);
      },
      (err) => console.error('events listener failed', err),
    );

    const removePrintersListener = onSnapshot(
      getAllPrintersQuery,
      (snapshot) => {
        const res: Printer[] = [
          { ref: 'noprint', id: NO_PRINT_ID, name: 'No printer' },
        ];
        snapshot.forEach((doc) => {
          res.push({ ref: doc.id, ...doc.data() } as Printer);
        });
        setPrinters(res);
      },
      (err) => console.error('printers listener failed', err),
    );

    const removeCustomFieldsListener = onSnapshot(
      getAllCustomFieldsQuery,
      (snapshot) => {
        // Soft-delete is filtered here (not in the Firestore query) so
        // (a) we don't need a composite index and (b) docs that predate
        // the `is_deleted` field still surface as not-deleted. See the
        // comment on `getAllCustomFieldsQuery` in `firebase.ts`.
        const res: CustomField[] = [];
        snapshot.forEach((doc) => {
          const data = { ref: doc.id, ...doc.data() } as CustomField;
          if (data.is_deleted === true) return;
          res.push(data);
        });
        setCustomFields(res);
      },
      (err) => console.error('custom-fields listener failed', err),
    );

    return () => {
      removeEventsListener();
      removePrintersListener();
      removeCustomFieldsListener();
    };
  }, []);

  const activeEvent = useMemo(
    () => events.find((e) => e.isActive),
    [events],
  );
  const activeEventRef = activeEvent?.ref;

  // Surfaces a warning toast when the admin flips the active event mid-session.
  // Volunteers' rosters and check-in dates swap out under them; without this,
  // it's silent and they might mistakenly check in someone from the new event.
  // The baseline is captured inside the events listener (above), so this
  // effect only fires on genuine post-mount transitions.
  useEffect(() => {
    const prev = prevActiveEventRefRef.current;
    // Listener hasn't delivered its first snapshot yet — nothing to compare.
    if (prev === undefined) return;
    const curr = activeEventRef ?? null;
    if (prev === curr) return;
    prevActiveEventRefRef.current = curr;
    if (curr) {
      const name = events.find((e) => e.ref === curr)?.name ?? 'unknown';
      toast.warning(
        `Active event changed to "${name}". Reload the page if you were mid-task.`,
      );
    } else {
      toast.warning('No event is active. Ask an admin to set one.');
    }
  }, [activeEventRef, events, toast]);

  // Per-event subscriptions tear down and re-establish whenever the active
  // event changes. While no event is active, every per-event list stays
  // empty (no doc references can be valid without an event partition).
  useEffect(() => {
    if (!activeEventRef) {
      setAttendees([]);
      setcheckInDates([]);
      setGroups([]);
      setRotations([]);
      return;
    }

    const removeAttendeesListener = onSnapshot(
      getAttendeesForEventQuery(activeEventRef),
      (snapshot) => {
        const res: Attendee[] = [];
        snapshot.forEach((doc) => {
          res.push({ ref: doc.id, ...doc.data() } as Attendee);
        });
        setAttendees(res);
      },
      (err) => console.error('attendees listener failed', err),
    );

    const removeCheckInDatesListener = onSnapshot(
      getCheckInDatesForEventQuery(activeEventRef),
      (snapshot) => {
        const res: CheckinDate[] = [];
        snapshot.forEach((doc) => {
          res.push({ ref: doc.id, ...doc.data() } as CheckinDate);
        });
        setcheckInDates(res);
      },
      (err) => console.error('check-in-dates listener failed', err),
    );

    const removeGroupsListener = onSnapshot(
      getGroupsForEventQuery(activeEventRef),
      (snapshot) => {
        const res: Group[] = [];
        snapshot.forEach((doc) => {
          res.push({ ref: doc.id, ...doc.data() } as Group);
        });
        setGroups(res);
      },
      (err) => console.error('groups listener failed', err),
    );

    const removeRotationsListener = onSnapshot(
      getRotationsForEventQuery(activeEventRef),
      (snapshot) => {
        const res: Rotation[] = [];
        snapshot.forEach((doc) => {
          res.push({ ref: doc.id, ...doc.data() } as Rotation);
        });
        setRotations(res);
      },
      (err) => console.error('rotations listener failed', err),
    );

    return () => {
      removeAttendeesListener();
      removeCheckInDatesListener();
      removeGroupsListener();
      removeRotationsListener();
    };
  }, [activeEventRef]);

  const activeCheckInDate = useMemo(() =>
    checkInDates.find(checkInDate => checkInDate.isActive),
    [checkInDates]
  );

  const removeAttendeeLocally = (ref: string) => {
    setAttendees((prev) => prev.filter((a) => a.ref !== ref));
  };

  const context = {
    events,
    activeEvent,
    attendees,
    checkInDates,
    activeCheckInDate,
    printers,
    groups,
    rotations,
    customFields,
    removeAttendeeLocally,
  };

  return (
    <FirebaseContext.Provider value={context}>
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebaseContext = () => {
  return useContext(FirebaseContext);
};
